// src/components/ContactSection.jsx
import React, { useState } from "react";
import { ArrowRight, Phone, MapPin, Send, Mail } from "lucide-react";
import emailjs from "@emailjs/browser";

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ type: "", message: "" });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setAlert({ type: "", message: "" });

    const serviceId = "service_cg3frhf";
    const templateId = "template_d52rjz5";
    const publicKey = "g9BKc7wYknHV_-uoj";

    const templateParams = {
      name: formData.name,
      email: formData.email,
      message: formData.message,
    };

    emailjs
      .send(serviceId, templateId, templateParams, publicKey)
      .then(() => {
        setAlert({ type: "success", message: "Message sent successfully!" });
        setFormData({ name: "", email: "", message: "" });
      })
      .catch(() => {
        setAlert({
          type: "error",
          message: "Failed to send message. Please try again later.",
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <section id="contact" className="bg-gray-50 py-16 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        {/* Top Text */}
        <div className="mb-14 text-center md:text-left">
          <button className="bg-gray-100 text-gray-800 px-4 py-1 rounded-md text-sm mb-4">
            Get in Touch
          </button>
          <h2 className="text-3xl md:text-5xl font-serif text-gray-900 mb-4 leading-tight">
            Let’s build something extraordinary together.
          </h2>
          <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto md:mx-0">
            Have a project idea, collaboration request, or need a reliable tech
            partner? Reach out to Riff Raff Tech Solutions — your trusted team
            for innovative software and digital solutions.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Left Side - Image + Info */}
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=80"
              alt="Office"
              className="rounded-2xl w-full h-[400px] sm:h-[500px] object-cover"
            />
            <div className="absolute bottom-6 left-4 right-4 sm:bottom-8 sm:left-8 sm:right-8 bg-gray-900/90 text-white p-5 sm:p-6 rounded-xl">
              <h3 className="text-xl sm:text-2xl font-serif mb-3">
                Have a Challenge or an Idea?
              </h3>
              <p className="text-gray-300 text-sm mb-6">
                Let’s discuss how Riff Raff Tech Solutions can help your
                business scale with custom web, mobile, and enterprise
                solutions.
              </p>

              <div className="space-y-5">
                {/* Phone Section */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-t border-gray-700 pt-3">
                  <div className="flex items-center gap-3 mb-2 sm:mb-0">
                    <Phone className="w-5 h-5 text-white" />
                    <div>
                      <p className="text-sm text-gray-400">Call us at:</p>
                      <p className="text-base font-medium">+91 9952749721</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white hidden sm:block" />
                </div>

                {/* Email Section */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-t border-gray-700 pt-3">
                  <div className="flex items-center gap-3 mb-2 sm:mb-0">
                    <Mail className="w-5 h-5 text-white" />
                    <div>
                      <p className="text-sm text-gray-400">Email us at:</p>
                      <p className="text-base max-md:text-sm font-medium break-all">
                        riffrafftechsolutions@gmail.com
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white hidden sm:block" />
                </div>

                {/* Address Section */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-t border-gray-700 pt-3">
                  <div className="flex items-center gap-3 mb-2 sm:mb-0">
                    <MapPin className="w-5 h-5 text-white" />
                    <div>
                      <p className="text-sm text-gray-400">Visit us at:</p>
                      <p className="text-base font-medium">
                        Tiruchirappalli, Tamil Nadu, India
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white hidden sm:block" />
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gray-900 text-white flex items-center justify-between p-5">
              <span className="text-lg font-medium">
                Book a Free Consultation
              </span>
              <Send className="w-5 h-5 text-white" />
            </div>

            <form
              onSubmit={handleSubmit}
              className="p-6 md:p-10 space-y-6 text-gray-700"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="w-full mt-2 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className="w-full mt-2 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us briefly about your project..."
                  className="w-full mt-2 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                  rows="4"
                  required
                ></textarea>
              </div>

              {alert.message && (
                <div
                  className={`p-3 rounded-md text-sm ${
                    alert.type === "success"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {alert.message}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full ${
                  loading ? "bg-gray-400" : "bg-black hover:bg-gray-800"
                } text-white font-medium py-3 rounded-lg flex items-center justify-center gap-2 transition`}
              >
                {loading ? "Sending..." : "Submit"}
                {!loading && <ArrowRight className="w-5 h-5 text-white" />}
              </button>

              <p className="text-xs text-gray-500 text-center md:text-left">
                By submitting this form, you agree to be contacted by Riff Raff
                Tech Solutions regarding your inquiry.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
